<script setup lang="ts">
  import { computed, useAttrs, useSlots } from 'vue';
  import { VBtn } from 'vuetify/components/VBtn';
  import { Tooltip as vTooltip } from 'vuetify/directives/tooltip';

  import { type BvBtnVariant, toBvBtnVariant, toVuetifyBtnVariant } from '@bv/components/form/button/variant';
  import BvIcon from '@bv/components/icon/BvIcon.vue';
  import type { BvIconValue } from '@bv/components/icon/partials/bv-icon-types.generated';
  import BvLoader from '@bv/components/icons/loading/BvLoader.vue';
  import { toSystemColor } from '@bv/utils/common/color/to-system-color';
  import { trans } from '@bv/utils/i18n';

  import type { RouteLocationRaw } from 'vue-router';

  defineOptions({
    inheritAttrs: false,
  });

  interface Props {
    text?: string;
    type?: 'button' | 'submit' | 'reset';
    /**
     * Variants
     * - primary
     * - secondary
     * - text
     * - outlined
     * - flat (deprecated) - Use `primary` instead
     * - tonal (deprecated) - Use `secondary` instead
     */
    variant?: BvBtnVariant;
    size?: 'sm' | 'md';
    hint?: string;
    color?: string;
    prependIcon?: BvIconValue;
    appendIcon?: BvIconValue;
    icon?: BvIconValue;
    rounded?: 0 | '0' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'pill' | 'circle';
    loading?: boolean;
    disabled?: boolean;
    ariaControls?: boolean;
    ariaHaspopup?: boolean;
    ariaExpanded?: boolean;
    to?: RouteLocationRaw;
    notExact?: boolean;
    href?: string;
    target?: '_blank' | '_self' | '_parent' | '_top';
  }

  const props = withDefaults(defineProps<Props>(), {
    text: undefined,
    type: 'button',
    variant: 'primary',
    size: 'md',
    color: 'base',
    hint: undefined,
    prependIcon: undefined,
    appendIcon: undefined,
    icon: undefined,
    rounded: 'lg',
    loading: false,
    disabled: false,
    ariaControls: false,
    ariaHaspopup: false,
    ariaExpanded: false,
    to: undefined,
    notExact: false,
    href: undefined,
    target: undefined,
  });

  const attrs = useAttrs();
  const slots = useSlots();

  const sizeClass = (size: Props['size']): string => {
    return size === 'sm' ? 'bv-btn--sm' : 'bv-btn--md';
  };

  const vuetifyVariant = computed(() => toVuetifyBtnVariant(props.variant));

  const hasDefaultSlotContent = computed(() => {
    const nodes = slots.default?.();
    return Boolean(nodes && nodes.length > 0);
  });

  const isIconOnly = computed(() => {
    return Boolean(props.icon) && !props.text && !hasDefaultSlotContent.value && !props.loading;
  });

  const iconSize = computed<'md' | 'lg'>(() => (props.size === 'sm' ? 'md' : 'lg'));

  const vBtnProps = computed(() => {
    return {
      id: attrs.id as string | undefined,
      'aria-controls': props.ariaControls,
      'aria-expanded': props.ariaExpanded,
      'aria-haspopup': props.ariaHaspopup,
      class: [
        'bv-btn min-w-0 tracking-normal normal-case',
        sizeClass(props.size),
        `bv-btn--${toBvBtnVariant(props.variant)}`,
        isIconOnly.value ? 'bv-btn--icon-only' : '',
        attrs.class,
      ],
      color: toSystemColor(props.color),
      disabled: props.disabled || props.loading,
      href: props.href,
      icon: isIconOnly.value ? true : undefined,
      rounded: props.rounded,
      target: props.target,
      to: props.to,
      type: props.type,
      variant: vuetifyVariant.value,
      exact: !props.notExact,
    };
  });

  const tooltipProps = computed(() => {
    return {
      text: props.hint ? trans(props.hint) : undefined,
      location: 'top center',
    };
  });

  const emit = defineEmits<{
    (event: 'click', e: MouseEvent): void;
    (event: 'mouseenter', e: MouseEvent): void;
    (event: 'mouseleave', e: MouseEvent): void;
  }>();
</script>

<template>
  <v-btn
    v-tooltip="tooltipProps"
    v-bind="vBtnProps"
    @click="emit('click', $event)"
    @mouseenter="emit('mouseenter', $event)"
    @mouseleave="emit('mouseleave', $event)"
  >
    <template
      v-if="!isIconOnly && !props.loading && props.prependIcon"
      #prepend
    >
      <bv-icon
        :icon="props.prependIcon"
        :size="iconSize"
      />
    </template>

    <template #default>
      <bv-icon
        v-if="isIconOnly"
        :icon="props.icon!"
        :size="iconSize"
      />
      <div
        v-else
        class="flex w-full items-center gap-3"
      >
        <bv-loader
          v-if="props.loading"
          class="h-4 w-4 min-w-4"
          color="currentcolor"
        />
        <template v-if="props.text">
          {{ trans(props.text) }}
        </template>
        <slot v-else />
      </div>
    </template>

    <template
      v-if="!isIconOnly && props.appendIcon"
      #append
    >
      <bv-icon
        :icon="props.appendIcon"
        :size="iconSize"
      />
    </template>
  </v-btn>
</template>

<style lang="scss" scoped>
  .bv-btn {
    border-radius: var(--bv-btn-radius) !important;
    cursor: pointer;
    outline: none;
    height: var(--bv-btn-height) !important;
    font-size: var(--bv-btn-font-size) !important;

    &.bv-btn--icon-only {
      width: var(--bv-btn-height);
    }

    &.bv-btn--primary {
      color: var(--white);
    }

    &.v-btn--disabled {
      cursor: not-allowed !important;
    }

    .v-btn__content {
      line-height: 1;
    }
  }
  .bv-btn--sm {
    --bv-btn-height: 2rem;
    --bv-btn-font-size: 0.875rem;
  }

  .bv-btn--md {
    --bv-btn-height: 2.5rem;
    --bv-btn-font-size: 1rem;
  }

  .bv-btn {
    :deep(.v-btn__prepend) {
      margin-inline-end: 0.75rem !important;
    }

    :deep(.v-btn__append) {
      margin-inline-start: 0.75rem !important;
    }
  }
</style>
